import { makeAutoObservable, observable } from 'mobx';

/**
 * Lightweight UI state for Content Studio screens
 * (clip/quote selection modals, highlight animations, etc.).
 *
 * Keeps transient visual state out of VideoCreatorStore.
 */
export class ContentStudioUIStore {
  recentlySavedClipIds: Set<string> = new Set();
  recentlySavedQuoteIds: Set<string> = new Set();

  /**
   * One-shot request for which tab the next clip-preview modal should open
   * on (e.g. the clip card's comment badge opens straight to Comments). The
   * modal reads it on mount and clears it.
   */
  requestedClipPreviewTab: 'comments' | null = null;

  constructor() {
    makeAutoObservable(this, {
      recentlySavedClipIds: observable.ref,
      recentlySavedQuoteIds: observable.ref,
    });
  }

  requestClipPreviewTab(tab: 'comments'): void {
    this.requestedClipPreviewTab = tab;
  }

  clearRequestedClipPreviewTab(): void {
    this.requestedClipPreviewTab = null;
  }

  // ── Clips ──────────────────────────────────────────────

  markClipsSaved(ids: string[]): void {
    this.recentlySavedClipIds = new Set(ids);
  }

  dismissClipHighlight(clipId: string): void {
    const next = new Set(this.recentlySavedClipIds);
    next.delete(clipId);
    this.recentlySavedClipIds = next;
  }

  isRecentlySavedClip(clipId: string): boolean {
    return this.recentlySavedClipIds.has(clipId);
  }

  // ── Quotes ─────────────────────────────────────────────

  markQuotesSaved(ids: string[]): void {
    this.recentlySavedQuoteIds = new Set(ids);
  }

  dismissQuoteHighlight(quoteId: string): void {
    const next = new Set(this.recentlySavedQuoteIds);
    next.delete(quoteId);
    this.recentlySavedQuoteIds = next;
  }

  isRecentlySavedQuote(quoteId: string): boolean {
    return this.recentlySavedQuoteIds.has(quoteId);
  }
}
